import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, useParams } from 'react-router'
import { AppLayout } from '@/components/layout/app-layout'
import { FullPageLoader } from '@/components/full-page-loader'
import { GuestOnly, Gate, RequireAuth } from './guards'
import type { Feature } from '@/lib/types'
import { setDemo } from '@/lib/demo'

const Dashboard = lazy(() => import('@/features/dashboard/dashboard-page'))
const CustomFields = lazy(() => import('@/features/settings/custom-fields-page'))
const Alerts = lazy(() => import('@/features/alerts/alerts-page'))
const Mcp = lazy(() => import('@/features/settings/mcp-page'))
const Ai = lazy(() => import('@/features/settings/ai-page'))
const Profile = lazy(() => import('@/features/profile/profile-page'))
const Assistant = lazy(() => import('@/features/assistant/assistant-page'))
const Feed = lazy(() => import('@/features/feed/feed-page'))
const Companies = lazy(() => import('@/features/companies/pages/companies-page'))
const Company = lazy(() => import('@/features/companies/pages/company-page'))
const CompanyForm = lazy(() => import('@/features/companies/pages/company-form-page'))
const TeamSettings = lazy(() => import('@/features/settings/team-page'))
const Subscription = lazy(() => import('@/features/settings/subscription-page'))
const PaymentResult = lazy(() => import('@/features/settings/payment-result-page'))
const SettingsHub = lazy(() => import('@/features/settings/settings-hub'))
const Crm = lazy(() => import('@/features/integrations/crm-page'))
const Hubspot = lazy(() => import('@/features/integrations/hubspot-page'))
const Zapier = lazy(() => import('@/features/integrations/zapier-page'))
const Slack = lazy(() => import('@/features/integrations/slack-page'))
const CompaniesImport = lazy(() => import('@/features/companies/pages/import-page'))
const Upgrade = lazy(() => import('@/pages/upgrade'))
const Login = lazy(() => import('@/features/auth/pages/login-page'))
const Register = lazy(() => import('@/features/auth/pages/register-page'))
const ForgotPassword = lazy(() => import('@/features/auth/pages/forgot-password-page'))
const SetPassword = lazy(() => import('@/features/auth/pages/set-password-page'))
const Impersonate = lazy(() => import('@/features/auth/pages/impersonate-page'))
const VerifyEmail = lazy(() => import('@/features/auth/pages/verify-email-page'))

function CompanyFormKeyed() {
  const { id } = useParams()
  return <CompanyForm key={id} />
}

const page = (node: ReactNode) => <Suspense fallback={null}>{node}</Suspense>
const admin = (node: ReactNode, feature?: Feature, paidOnly?: boolean) => (
  <Gate roles={['admin']} feature={feature} paidOnly={paidOnly}>
    {node}
  </Gate>
)

// Paths and guards match v1 src/routes/MainRoutes.js.
// dev-only demo mode: fake user + in-memory API (lib/demo.ts)
function DemoSwitch({ on }: { on: boolean }) {
  setDemo(on)
  window.location.replace(on ? '/' : '/login')
  return null
}
const demo = import.meta.env.DEV
  ? [
      { path: '/__demo', element: <DemoSwitch on /> },
      { path: '/__demo/off', element: <DemoSwitch on={false} /> },
    ]
  : []

export const router = createBrowserRouter([
  ...demo,
  {
    element: <GuestOnly />,
    children: [
      { path: '/login', element: page(<Login />) },
      { path: '/register', element: page(<Register />) },
      { path: '/forgot-password', element: page(<ForgotPassword />) },
    ],
  },
  // admin panel's "Sign in as": signs in with the token in the URL fragment
  { path: '/impersonate', element: page(<Impersonate />) },
  // reachable signed in or out (the link arrives by email)
  { path: '/reset-password', element: page(<SetPassword />) },
  { path: '/verify-email', element: <Suspense fallback={<FullPageLoader />}><VerifyEmail /></Suspense> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: page(<Dashboard />) },
          { path: '/feed', element: page(<Feed />) },
          { path: '/companies', element: page(<Companies />) },
          { path: '/companies/details/:crmId', element: page(<Company />) },
          { path: '/companies/create', element: admin(page(<CompanyForm />)) },
          // key: the form keeps no state across records
          { path: '/companies/details/:parentId/edit/:id', element: admin(page(<CompanyFormKeyed />)) },
          { path: '/companies/import/xlsx', element: admin(page(<CompaniesImport />)) },
          { path: '/ai-chat', element: page(<Assistant />) },
          { path: '/settings', element: admin(page(<SettingsHub />)) },
          { path: '/settings/team', element: admin(page(<TeamSettings />)) },
          { path: '/settings/subscription', element: admin(page(<Subscription />)) },
          { path: '/settings/subscription/success', element: admin(page(<PaymentResult ok />)) },
          { path: '/settings/subscription/error', element: admin(page(<PaymentResult ok={false} />)) },
          // v1's plan picker page; the chooser is now a dialog on the subscription page
          { path: '/settings/subscription/plan', element: <Navigate to="/settings/subscription?upgrade" replace /> },
          { path: '/settings/crm-integrations', element: admin(page(<Crm />)) },
          { path: '/settings/hubspot', element: admin(page(<Hubspot />), 'crm_integrations') },
          { path: '/settings/zapier', element: admin(page(<Zapier />), 'crm_integrations') },
          { path: '/settings/custom-fields', element: admin(page(<CustomFields />), undefined, true) },
          // open on every plan: a Free team sees what Premium unlocks, read-only
          { path: '/city-settings/alerts', element: admin(page(<Alerts />)) },
          { path: '/settings/mcp', element: admin(page(<Mcp />)) },
          { path: '/settings/slack', element: admin(page(<Slack />)) },
          // open on every plan: a Free team sees what Premium adds
          { path: '/settings/ai', element: admin(page(<Ai />)) },
          // v1 kept this admin-only; it's personal, so every member gets it
          { path: '/profile', element: page(<Profile />) },
          { path: '/plan/upgrade', element: page(<Upgrade />) },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
])
